import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Ban, CheckCircle2, Coins, Gauge, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { useCredits } from "../../api/queries";
import { Alert, Badge, Button, Card, Field, Modal, NumberInput, PageHead, Select, Spinner, Switch, TextInput, useToast } from "../../components/ui";
import { date, initials } from "../../lib/format";

function GrantForm({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<S["CreditGrantKind"]>("grant");
  const [amount, setAmount] = useState<number | null>(10);
  const [expires, setExpires] = useState("");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const grant = useMutation({
    mutationFn: () => {
      const body: S["CreditGrant"] = { kind, amount: amount ?? 0, note: note || undefined };
      if (kind === "trial" && expires) body.expires_at = new Date(expires).toISOString();
      if (kind === "refund" && ref.trim()) {
        // A website quote id or a design id: the backend resolves either.
        if (ref.startsWith("q:")) body.public_quote_id = ref.slice(2).trim();
        else body.design_id = ref.trim();
      }
      return unwrap(api.POST("/v1/admin/orgs/{org_id}/credits", { params: { path: { org_id: orgId } }, body }));
    },
    onSuccess: (e) => {
      toast(`${e.amount > 0 ? "+" : ""}${e.amount} credits (${e.kind})`);
      setNote("");
      setRef("");
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <div className="stack">
      <div className="form-grid">
        <Field label="Type">
          <Select
            value={kind}
            onChange={(k) => {
              setKind(k);
              if (k === "refund") setAmount(1); // a design consumed one credit
            }}
            options={[
              { value: "grant", label: "Grant (goodwill, sales)" },
              { value: "trial", label: "Trial (may expire)" },
              { value: "refund", label: "Refund a design" },
            ]}
          />
        </Field>
        <Field label="Credits">
          <NumberInput value={amount} min={1} max={100000} step={1} onChange={setAmount} />
        </Field>
        {kind === "trial" && (
          <Field label="Expires" hint="Empty: never" className="span-2">
            <input className="input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </Field>
        )}
        {kind === "refund" && (
          <Field label="Design id" hint="Or q:<public quote id> for a website quote" className="span-2">
            <TextInput value={ref} onChange={(e) => setRef(e.target.value)} placeholder="9c1d…" className="input mono" />
          </Field>
        )}
        <Field label="Note (audited)" className="span-2">
          <TextInput value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Why — visible in their ledger" />
        </Field>
      </div>
      <div>
        <Button variant="primary" icon={<Coins />} disabled={!amount || amount < 1} loading={grant.isPending} onClick={() => grant.mutate()}>
          {kind === "refund" ? "Refund" : "Grant"} {amount ?? 0} credit{amount === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
}

function SchedulingForm({ orgId, scheduling, onDone }: { orgId: string; scheduling: S["OrganizationScheduling"]; onDone: () => void }) {
  const toast = useToast();
  const [s, setS] = useState(scheduling);
  useEffect(() => setS(scheduling), [scheduling]);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/admin/orgs/{org_id}/scheduling", { params: { path: { org_id: orgId } }, body: { ...s, lane: s.lane || null } })),
    onSuccess: () => {
      toast("Scheduling saved");
      onDone();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const dirty = JSON.stringify(s) !== JSON.stringify(scheduling);
  return (
    <div className="stack">
      <div className="form-grid">
        <Field label="Service tier" hint="Priority: enterprise 10, paid 50, free 100">
          <Select value={s.service_tier} onChange={(v) => setS({ ...s, service_tier: v })} options={["free", "paid", "enterprise"]} />
        </Field>
        <Field label="Priority override" hint="Lower runs sooner; empty = tier">
          <NumberInput value={s.priority_override} step={1} onChange={(v) => setS({ ...s, priority_override: v == null ? null : Math.round(v) })} />
        </Field>
        <Field label="Dedicated lane" hint="Tasks go to <class>@lane queues">
          <TextInput value={s.lane ?? ""} placeholder="none" onChange={(e) => setS({ ...s, lane: e.target.value.toLowerCase() || null })} className="input mono" />
        </Field>
        <Field label="Running cap per queue" hint="Empty = platform default">
          <NumberInput value={s.max_running_override} min={0} step={1} onChange={(v) => setS({ ...s, max_running_override: v == null ? null : Math.round(v) })} />
        </Field>
        <div className="span-2">
          <Switch checked={s.lane_exclusive} onChange={(v) => setS({ ...s, lane_exclusive: v })} label="Exclusive lane (no spill-over to shared workers)" />
        </div>
      </div>
      <div>
        <Button icon={<Save />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
          Save scheduling
        </Button>
      </div>
    </div>
  );
}

export function AdminOrgDetail() {
  const { orgId = "" } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState<null | S["OrganizationStatus"]>(null);
  const [note, setNote] = useState("");
  const org = useQuery({
    queryKey: ["admin-org", orgId],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs/{org_id}", { params: { path: { org_id: orgId } } })),
  });
  const ledger = useCredits(orgId, 15);
  const setStatus = useMutation({
    mutationFn: (status: S["OrganizationStatus"]) =>
      unwrap(api.PUT("/v1/admin/orgs/{org_id}/status", { params: { path: { org_id: orgId } }, body: { status, note: note || undefined } })),
    onSuccess: (o) => {
      toast(o.status === "suspended" ? "Organisation suspended" : "Organisation reactivated");
      setConfirm(null);
      setNote("");
      refresh();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-org", orgId] });
    qc.invalidateQueries({ queryKey: ["credits", orgId] });
    qc.invalidateQueries({ queryKey: ["admin-orgs"] });
  };

  if (org.isLoading) return <Spinner />;
  if (org.error || !org.data) return <Alert tone="danger">{errorMessage(org.error)}</Alert>;
  const { organization: o, balance, members, embed_keys, scheduling } = org.data;
  const suspended = o.status === "suspended";

  return (
    <>
      <PageHead
        crumbs={
          <Link to="/admin/orgs">
            <ArrowLeft size={12} style={{ verticalAlign: -1 }} /> Organisations
          </Link>
        }
        title={
          <span className="row" style={{ gap: 12 }}>
            <span className="org-avatar" style={{ width: 38, height: 38, fontSize: 15 }}>{initials(o.name)}</span>
            {o.name}
            <Badge tone={suspended ? "danger" : "ok"}>{o.status}</Badge>
          </span>
        }
        description={<span className="mono small">{o.id}</span>}
        actions={
          suspended ? (
            <Button variant="primary" icon={<CheckCircle2 />} onClick={() => setConfirm("active")}>
              Reactivate
            </Button>
          ) : (
            <Button variant="danger" icon={<Ban />} onClick={() => setConfirm("suspended")}>
              Suspend
            </Button>
          )
        }
      />
      {suspended && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="danger" title="Suspended.">Their embed keys are refused; the calculator shows their contact details.</Alert>
        </div>
      )}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <div className="card stat">
          <div className="label">Credits</div>
          <div className="value">{balance}</div>
        </div>
        <div className="card stat">
          <div className="label">Plan</div>
          <div className="value" style={{ fontSize: 20 }}>{o.plan || "free"}</div>
          <div className="sub">tier {scheduling.service_tier}</div>
        </div>
        <div className="card stat">
          <div className="label">Trial ends</div>
          <div className="value" style={{ fontSize: 20 }}>{o.trial_ends_at ? date(o.trial_ends_at) : "—"}</div>
        </div>
        <div className="card stat">
          <div className="label">Active embed keys</div>
          <div className="value">{embed_keys}</div>
          <div className="sub">
            {o.country || "—"} · {o.currency || "—"} · since {date(o.created_at)}
          </div>
        </div>
      </div>
      <div className="split">
        <div className="stack">
          <Card title="Credits" description="Grants, trials and refunds are written to their ledger and the audit trail.">
            <GrantForm orgId={orgId} onDone={refresh} />
          </Card>
          <Card title="Ledger" pad={false}>
            {ledger.data?.entries.length ? (
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {ledger.data.entries.map((e) => (
                      <tr key={e.id}>
                        <td className="nowrap small">{date(e.created_at, true)}</td>
                        <td>
                          <Badge plain>{e.kind}</Badge>
                        </td>
                        <td className="small muted ellipsis" style={{ maxWidth: 200 }}>
                          {e.note || "—"}
                        </td>
                        <td className="num" style={{ fontWeight: 600, color: e.amount > 0 ? "var(--ok)" : undefined }}>
                          {e.amount > 0 ? `+${e.amount}` : e.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="card-body muted">No entries.</div>
            )}
          </Card>
        </div>
        <div className="stack">
          <Card title="Members" pad={false}>
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id}>
                      <td>
                        <div className="cell-main">{m.display_name || m.email}</div>
                        {m.display_name && <div className="cell-sub">{m.email}</div>}
                      </td>
                      <td className="right">
                        <Badge plain tone={m.role === "owner" ? "info" : undefined}>{m.role}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card title={<span className="row" style={{ gap: 8 }}><Gauge size={16} /> Scheduling</span>} description="Queue priority, dedicated workers and caps for their design jobs.">
            <SchedulingForm orgId={orgId} scheduling={scheduling} onDone={refresh} />
          </Card>
        </div>
      </div>
      {confirm && (
        <Modal
          title={confirm === "suspended" ? `Suspend ${o.name}?` : `Reactivate ${o.name}?`}
          onClose={() => setConfirm(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button variant={confirm === "suspended" ? "danger" : "primary"} loading={setStatus.isPending} onClick={() => setStatus.mutate(confirm)}>
                {confirm === "suspended" ? "Suspend" : "Reactivate"}
              </Button>
            </>
          }
        >
          <div className="stack">
            <p className="muted">
              {confirm === "suspended"
                ? "Their website calculator stops working at once. Members can still sign in."
                : "Their embed keys work again immediately."}
            </p>
            <Field label="Note (audited)">
              <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason" />
            </Field>
          </div>
        </Modal>
      )}
    </>
  );
}
